import type { TFunction } from "i18next";

import { isAnswered } from "@memorang/helpers/item";
import { getSubmittedRawInput } from "@memorang/helpers/user-submitted-answer";
import type { BaseItemSchema } from "@memorang/types";
import {
	isAllBlanksFilled,
	isBlankDragDrop,
	isBlankDropdown,
	isMatchingPair,
	isMultiSelect,
} from "../../helpers/item-type-guards";
import { getNumChoicesRequired } from "../../helpers/session";
import type { SessionState } from "../../hooks/types";

const getChoiceRequirement = (item: BaseItemSchema) => {
	const { response, scoring } = item;
	const selectionRequired =
		scoring?.type === "ANSWER_KEY_SELECTION" || scoring?.type === "choice"
			? getNumChoicesRequired(scoring)
			: undefined;
	const minChoiceCount =
		(response.type === "choice" ? response.minCorrectChoices : undefined) ??
		selectionRequired?.min;
	return { minChoiceCount, isMultiChoice: isMultiSelect(item) };
};

export const getShouldShowConfidencesSubmitButtons = (
	sessionState: SessionState,
	currentItemIndex: number,
) => {
	const {
		itemStates,
		answers,
		sessionItems,
		currentBlockIndex,
		isSummativeTest,
		inReviewMode,
		formStates,
	} = sessionState;

	if (isSummativeTest || inReviewMode) {
		return false;
	}
	const { id } = sessionItems[currentBlockIndex][currentItemIndex];

	const currentItem = sessionItems[currentBlockIndex][currentItemIndex];
	const response = currentItem.response;

	const currentItemState = itemStates?.get(id!);
	const currentAnswer = answers?.get(id!);

	const { minChoiceCount, isMultiChoice } = getChoiceRequirement(currentItem);

	const selectedChoiceIds = currentItemState?.selectedChoiceIds || [];

	const getIsSelected = () => {
		if (
			isBlankDragDrop(currentItem) ||
			isBlankDropdown(currentItem) ||
			isMatchingPair(currentItem)
		) {
			return isAllBlanksFilled(currentItem, formStates?.[id!]);
		}
		if (isMultiChoice) {
			return selectedChoiceIds.length >= (minChoiceCount ?? 1);
		}
		if (response?.type === "form") {
			return Boolean(currentItemState?.selectedChoiceIds?.[0]);
		}
		return selectedChoiceIds?.length > 0;
	};
	const isSelected = getIsSelected();

	return (
		isSelected && !isAnswered(currentAnswer) && !isAnswered(currentItem.answer)
	);
};

export type NextButtonSessionState = Pick<
	SessionState,
	| "sessionItems"
	| "itemStates"
	| "inReviewMode"
	| "currentBlockIndex"
	| "formStates"
>;

export const getNextButtonStates = (
	sessionState: NextButtonSessionState,
	currentItemIndex: number,
	t: TFunction,
	currentUserResponse?: unknown,
) => {
	const {
		sessionItems,
		itemStates,
		inReviewMode,
		currentBlockIndex,
		formStates,
	} = sessionState;

	const currentItem = sessionItems[currentBlockIndex][currentItemIndex];
	const { id, response } = currentItem;

	const { minChoiceCount, isMultiChoice } = getChoiceRequirement(currentItem);
	const currentItemState = itemStates?.get(id!);

	const selectedChoiceIds =
		currentItemState?.selectedChoiceIds ||
		currentItem.answer?.selectedChoiceIds ||
		[];

	const currentOrders = currentItemState?.currentOrders;

	const hasPersistedBlankValues =
		Object.keys(currentItem.answer?.selectedBlankValues ?? {}).length > 0;
	const hasPersistedOrder =
		(currentItem.answer?.selectedOrder?.length ?? 0) > 0;
	const hasPersistedUserInput =
		getSubmittedRawInput(currentItem.answer) != null ||
		Boolean(currentItem.answer?.userResponseText);

	const getIsSelected = () => {
		if (inReviewMode) {
			return true;
		}
		if (isMultiChoice) {
			return (
				selectedChoiceIds?.length >= (minChoiceCount ?? 1) ||
				currentItemState?.answered
			);
		}
		if (response?.type === "form") {
			if (currentItemState?.answered || isAnswered(currentItem.answer)) {
				return true;
			}
			return Boolean(currentItemState?.selectedChoiceIds?.[0]);
		}
		return (
			(selectedChoiceIds && selectedChoiceIds.length > 0) ||
			(currentOrders && currentOrders.length > 0) ||
			Boolean(currentUserResponse) ||
			currentItemState?.answered ||
			hasPersistedBlankValues ||
			hasPersistedOrder ||
			hasPersistedUserInput
		);
	};

	const isSelected = getIsSelected();

	if (
		isMultiChoice &&
		selectedChoiceIds.length > 0 &&
		minChoiceCount &&
		!inReviewMode
	) {
		const isMinChoiceCountValid = selectedChoiceIds.length >= minChoiceCount;
		if (!isMinChoiceCountValid) {
			return {
				text: t("session.navigation.next"),
				showButtonNavSkip: false,
				disabled: true,
				tooltip: {
					next: t("session.navigation.selectMoreChoices", {
						count: minChoiceCount,
						defaultValue: `Select at least ${minChoiceCount} choices to continue.`,
					}),
				},
			};
		}
	}
	if (
		isBlankDragDrop(currentItem) ||
		isBlankDropdown(currentItem) ||
		isMatchingPair(currentItem)
	) {
		const allFilled = isAllBlanksFilled(currentItem, formStates?.[id!]);
		return {
			text: allFilled
				? t("session.navigation.next")
				: t("session.navigation.skip"),
			showButtonNavSkip: !allFilled,
			disabled: false,
			tooltip: undefined,
		};
	}

	return {
		text: isSelected
			? t("session.navigation.next")
			: t("session.navigation.skip"),
		showButtonNavSkip: !(isSelected || inReviewMode),
		disabled: false,
		tooltip: undefined,
	};
};

export const getShowButtonNavFinish = (
	{ answers, inReviewMode, currentBlockIndex, sessionItems }: SessionState,
	currentItemIndex: number,
) => {
	const currentBlock = sessionItems[currentBlockIndex];
	const currentItem = currentBlock[currentItemIndex];
	const currentAnswer = answers?.get(currentItem.id!);
	const sessionLength = currentBlock.length;

	return (
		(isAnswered(currentAnswer) || inReviewMode) &&
		currentItemIndex === sessionLength - 1
	);
};
